import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";

export interface UserLookup {
  id: string;
  full_name: string;
  email: string;
  avatar_url: string | null;
  monthly_capacity_hours: number | null;
  role: string;
}

/**
 * Roller som ANSES som ansatte i ansatt-velgere (PL, kunderådgiver,
 * timetildeling, etc.). External-brukere (regnskapsfører/revisor) er
 * innlogget for å godkjenne bilag/lese, men skal ikke kunne tildeles
 * prosjektarbeid. Superadmin er ekskludert fordi det er en
 * vedlikeholds-/system-rolle (typisk Vibbing-utviklere + test-brukere) og
 * skal ikke dukke opp som mulig tildeling i produksjons-flyten. Hold listen
 * her synk med systemroller-CRUD.
 */
const ASSIGNABLE_ROLES = new Set(["user", "admin"]);

/** Brukes i pickers som tildeler arbeid. */
export function isAssignableUser(user: { role: string }): boolean {
  return ASSIGNABLE_ROLES.has(user.role);
}

/**
 * Ansatt = alt UNNTATT external (VIB-1055). Bredere enn isAssignableUser:
 * inkluderer superadmin, fordi chat skal være tilgjengelig for alle interne,
 * ikke bare de som kan tildeles produksjonsarbeid. External (regnskapsfører/
 * revisor) skal ikke ha chat-tilgang i det hele tatt.
 */
export function isEmployeeUser(user: { role: string }): boolean {
  return user.role !== "external";
}

export function useUsers() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["users"],
    queryFn: async (): Promise<UserLookup[]> => {
      const { data, error } = await supabase
        .from("users")
        .select("id, full_name, email, avatar_url, monthly_capacity_hours, role")
        .eq("is_active", true);
      if (error) throw error;
      // Alfabetisk default — pickers og lister kan ellers vise insert-rekkefølge,
      // som er forvirrende når brukerne har samme rolle.
      return (data ?? []).slice().sort((a, b) =>
        (a.full_name ?? "").localeCompare(b.full_name ?? "", "no"),
      );
    },
    // Vent på innlogget sesjon. Kalles hooken før sesjonen er klar (fra en
    // provider eller før `AuthGate` vet om brukeren), går spørringen som anon,
    // feiler med 401 og prøves bare én gang til. Da sto tilgangene feilet for
    // resten av økten, og `/` sendte brukeren til Hjelp.
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
  });
}

export interface UserAccount {
  id: string;
  full_name: string;
  email: string;
  role: string;
  is_active: boolean;
  /** CRM-4.0: kan bookes som vert for kundemøte. */
  can_host_meetings: boolean;
  /** VIB-2917: avdeling (Oslo/Bergen). */
  department_id: string | null;
  /** VIB-2917: utstyrsansvarlig. */
  can_manage_equipment: boolean;
}

/**
 * Henter ALLE kontoer inkl. inaktive/eksterne/superadmin. Brukes av
 * Brukere-siden (/innstillinger/brukere). Skiller seg fra useUsers() som
 * filtrerer is_active=true for lookup-formål.
 */
export function useAllUsers() {
  return useQuery({
    queryKey: ["users-all"],
    queryFn: async (): Promise<UserAccount[]> => {
      const { data, error } = await supabase
        .from("users")
        .select("id, full_name, email, role, is_active, can_host_meetings, department_id, can_manage_equipment")
        .order("full_name");
      if (error) throw error;
      return (data ?? []) as UserAccount[];
    },
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Subset av useUsers som filtrerer ut external-brukere. Brukes i alle
 * UI-velgere der vi tildeler ARBEID (timer, oppgaver, prosjekt-roller).
 * Bruk useUsers() direkte hvis du vil ha hele lookup-listen for å vise
 * navn/avatar på brukere som allerede er knyttet til noe.
 */
export function useAssignableUsers() {
  const query = useUsers();
  const data = useMemo(
    () => (query.data ?? []).filter((u) => ASSIGNABLE_ROLES.has(u.role)),
    [query.data],
  );
  return { ...query, data };
}

/** Map user.id (UUID) -> UserLookup */
export function buildUserMap(users: UserLookup[]): Map<string, UserLookup> {
  const map = new Map<string, UserLookup>();
  for (const u of users) {
    map.set(u.id, u);
  }
  return map;
}
